import { MouseEvent, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Checkbox, Chip, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { getTitleEpisodes, replaceSelectedVersions, resetSelectedTitle, updateSelectedSeasonFilter } from '@/redux/slices/titleNavigationDrawerSlice';
import {
  selectSeasonEpisodes,
  selectStatusById,
  selectTitleFromTitlesMap,
  selectTitleNavigationDrawerSlice
} from '@/redux/selectors/titleNavigationDrawerSelectors';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { LoadingStatuses } from '@/constants/constants';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TitleItem from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/TitleItem';
import SeasonEpisodesListSkeleton from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/SelectedTitleView/SeasonEpisodesListSkeleton';
import SeasonEpisodesList from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/SelectedTitleView/SeasonEpisodesList';

const SelectedTitleView = () => {
  const dispatch = useAppDispatch();
  const { selectedTitle, selectedSeasonFilter, selectedTitlesMap, selectedVersions } = useAppSelector(selectTitleNavigationDrawerSlice);
  const title = useAppSelector(selectTitleFromTitlesMap);
  const seasonStatus = useAppSelector((state) => selectStatusById(state, selectedTitle?.id));
  const seasonIsPending = seasonStatus === LoadingStatuses.pending;
  const seasonIsError = seasonStatus === LoadingStatuses.rejected;
  const episodesStatus = useAppSelector((state) => selectStatusById(state, selectedSeasonFilter?.id));
  const episodesIsPending = episodesStatus === LoadingStatuses.pending;
  const episodesIsError = episodesStatus === LoadingStatuses.rejected;
  const seasonEpisodes = useAppSelector(selectSeasonEpisodes);
  const filters = useAppSelector(selectDataTableFilters);
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const goBack = () => {
    dispatch(resetSelectedTitle());
  };

  const filterBySeason = (season: TitleEntityV2) => {
    dispatch(updateSelectedSeasonFilter(season));
  };

  const showMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const selectAllByVersionGroup = (newVersions: TitleEntityV2[], isSelected: boolean) => {
    if (isSelected) {
      const newSelectedVersions = selectedVersions.filter((sv) => !newVersions.find((nv) => nv.id === sv.id));
      dispatch(replaceSelectedVersions(newSelectedVersions));
    } else {
      const newSelectedVersions = [...new Map([...selectedVersions, ...newVersions].map((version) => [version.id, version])).values()];
      dispatch(replaceSelectedVersions(newSelectedVersions));
    }
  };

  const uniqueVersionGroups = useMemo(() => {
    if (!selectedSeasonFilter || !selectedTitlesMap?.[selectedSeasonFilter.id]) return [];
    return (
      selectedTitlesMap[selectedSeasonFilter.id].children
        .flatMap((ep) => (selectedTitlesMap?.[ep.id] ? [...selectedTitlesMap[ep.id].children] : []))
        .reduce(
          (acc, cur) => {
            const name = cur.name;

            if (!acc.find((v) => v.name === name)) {
              acc.push({ name, versions: [cur] });
            } else {
              acc.find((v) => v.name === name)?.versions.push(cur);
            }

            return acc;
          },
          [] as { name: TitleEntityV2['name']; versions: TitleEntityV2[] }[]
        ) ?? []
    );
  }, [selectedSeasonFilter, selectedTitlesMap]);

  useEffect(
    function fetchTitleEpisodes() {
      if (
        !selectedTitle ||
        !selectedSeasonFilter ||
        selectedSeasonFilter.relationships.children.length === 0 ||
        selectedTitlesMap?.[selectedSeasonFilter.id]
      )
        return;
      dispatch(getTitleEpisodes(selectedSeasonFilter));
    },
    [selectedTitle, selectedSeasonFilter]
  );

  useEffect(
    function updateSelectedVersionsFromURLOnLoad() {
      if (Object.keys(filters).length === 0 || !filters?.['title_alt_client_id_filter']?.length) return;
      const alphaIds = filters['title_alt_client_id_filter'].split(',');
      const isSelected = alphaIds.every((aid: string) => selectedVersions.find((version) => version.alpha_id === aid));
      if (isSelected) return;
      const versionsToAdd = uniqueVersionGroups.flatMap(({ versions }) => versions.flatMap((v) => v)).filter((v) => alphaIds.includes(v.alpha_id));
      if (versionsToAdd.length === 0) return;
      dispatch(replaceSelectedVersions([...new Map([...selectedVersions, ...versionsToAdd].map((version) => [version.id, version])).values()]));
    },
    [selectedVersions, filters, uniqueVersionGroups]
  );

  if (!selectedTitle) return null;

  return (
    <>
      <GoBackButton variant="text" startIcon={<PolarisIcon icon="left" />} onClick={goBack}>
        Go back
      </GoBackButton>
      <TitleItem isSelected title={selectedTitle} />
      {seasonIsPending && <Skeleton variant="rounded" height={80} />}
      {seasonIsError && (
        <Typography color="textSecondary" textAlign="center">
          No seasons found
        </Typography>
      )}
      {!seasonIsPending && !seasonIsError && title && (
        <Wrapper>
          <SeasonsChipsContainer>
            {title.children.map((season) => (
              <SeasonFilterChip
                key={`season-chip-${season.id}`}
                label={`S${Math.trunc(season.sequence_order)}`}
                isSelected={selectedSeasonFilter?.id === season.id}
                onClick={() => filterBySeason(season)}
              />
            ))}
          </SeasonsChipsContainer>
          {!episodesIsPending && !episodesIsError && seasonEpisodes.length > 0 && (
            <>
              <SelectAllButton variant="contained" endIcon={<PolarisIcon icon="down" height={14} />} onClick={showMenu}>
                Select All
              </SelectAllButton>
              <SelectAllMenu open={open} anchorEl={anchorEl} onClose={closeMenu}>
                {uniqueVersionGroups.length === 0 && <ListItemText primary={'No Versions Found'} />}
                {uniqueVersionGroups.map(({ name, versions }) => {
                  const hasSelection = Boolean(versions.find((version) => selectedVersions.find((sv) => sv.id === version.id)));
                  const allSelected = versions.every((version) => selectedVersions.find((sv) => sv.id === version.id));

                  return (
                    <GroupVersionItem key={`version-group-${name}`} onClick={() => selectAllByVersionGroup(versions, allSelected)}>
                      <ListItemText primary={name} />
                      <ListItemIcon>
                        <Checkbox edge="end" indeterminate={!allSelected && hasSelection} checked={allSelected} disableRipple />
                      </ListItemIcon>
                    </GroupVersionItem>
                  );
                })}
              </SelectAllMenu>
              <SeasonEpisodesList />
            </>
          )}
          {episodesIsPending && <SeasonEpisodesListSkeleton />}
          {(episodesIsError || (!episodesIsPending && selectedSeasonFilter?.relationships.children.length === 0)) && (
            <Typography color="textSecondary" textAlign="center">
              No episodes found
            </Typography>
          )}
        </Wrapper>
      )}
    </>
  );
};

export default SelectedTitleView;

const GoBackButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  width: 'fit-content'
}));

const Wrapper = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(2)
}));

const SeasonsChipsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  overflow: 'scroll',
  maxWidth: '100%',
  overscrollBehavior: 'contain',
  '&::-webkit-scrollbar-corner': {
    background: 'transparent'
  }
}));

const SeasonFilterChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: isSelected ? theme.palette.secondary.main : 'transparent',
  color: isSelected ? theme.palette.secondary.contrastText : theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 600,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.secondary.main
  }
}));

const SelectAllButton = styled(Button)(() => ({
  width: 'fit-content'
}));

const SelectAllMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    minWidth: 200
  }
}));

const GroupVersionItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  justifyContent: 'space-between',
  '& .MuiListItemIcon-root': {
    justifyContent: 'end'
  }
}));
